import { LINKS, CAMPAIGN_IMAGES } from "../../config/links";

// Live campaign data
const FUNNEL = [
  { label: "Total Leads", value: 302, pct: 100, tone: "slate" },
  { label: "Eligible Leads", value: 201, pct: 66.56, tone: "slate" },
  { label: "Booked Appointments", value: 140, pct: 69.65, tone: "amber" },
];

const OUTCOMES = [
  { label: "Turning 65 — Not Ready", value: 68, pct: 22.52, tone: "amber" },
  { label: "No-Shows", value: 39, pct: 27.86, tone: "rose" },
  { label: "Not Interested", value: 24, pct: 11.94, tone: "slate" },
  { label: "Bad Numbers", value: 21, pct: 6.95, tone: "slate" },
];

const TONE = {
  amber: { text: "text-amber-400", bar: "bg-amber-500" },
  emerald: { text: "text-emerald-400", bar: "bg-emerald-500" },
  rose: { text: "text-rose-400", bar: "bg-rose-500" },
  slate: { text: "text-slate-300", bar: "bg-slate-500" },
};

export default function Proof() {
  const roi = (((4200 - 3047.58) / 3047.58) * 100).toFixed(1);

  return (
    <section
      id="proof"
      data-testid="proof-section"
      className="relative pt-16 pb-24 sm:pt-20 sm:pb-32 overflow-hidden border-y border-white/5"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1733]/80 via-[#0a1733]/60 to-[#0a1733]/80" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[500px] w-[1000px] bg-amber-500/[0.05] blur-[140px] rounded-full" aria-hidden />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* HEADER */}
        <div className="max-w-3xl mb-12">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-[1px] w-10 bg-amber-500/70" />
            <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
              Operational Proof
            </span>
          </div>
          <h2
            data-testid="proof-headline"
            className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
            style={{ transitionDelay: "80ms" }}
          >
            Real systems.{" "}
            <span className="italic text-amber-400">Real results.</span>
          </h2>
          <p
            className="reveal mt-5 text-slate-300 text-base sm:text-lg leading-relaxed"
            style={{ transitionDelay: "140ms" }}
          >
            Leads alone don't create revenue — systems do. Here's what the
            current advisor funnel actually produced after optimization.
          </p>
        </div>

        {/* TOPLINE TILES */}
        <div
          data-testid="proof-topline"
          className="reveal mb-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10"
        >
          <ToplineTile label="Total Leads" value="302" tone="slate" />
          <ToplineTile label="Booked Appointments" value="140" tone="amber" sub="69.65% of eligible" />
          <ToplineTile label="Revenue Generated" value="$4.2K" tone="emerald" sub={`+${roi}% ROI`} />
        </div>

        {/* FUNNEL */}
        <div className="reveal grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 mb-8">
          {/* Funnel chart */}
          <div className="lg:col-span-7 bg-[#0a1733]/80 p-7 lg:p-10">
            <div className="flex items-center justify-between mb-7">
              <div>
                <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-1">
                  Conversion Funnel
                </div>
                <div className="font-display text-xl text-white">
                  From inquiry → income
                </div>
              </div>
            </div>

            <ul className="space-y-5">
              {FUNNEL.map((row, i) => {
                const t = TONE[row.tone];
                const width = Math.max((row.value / 302) * 100, 6);
                return (
                  <li
                    key={row.label}
                    data-testid={`funnel-row-${i}`}
                    className="reveal"
                    style={{ transitionDelay: `${80 + i * 70}ms` }}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-400">
                        {row.label}
                      </span>
                      <span className="flex items-baseline gap-2">
                        <span className={`font-display text-2xl ${t.text}`}>
                          {row.value}
                        </span>
                        {row.pct < 100 && (
                          <span className="font-sans-body text-[10px] text-slate-500 uppercase tracking-[0.18em]">
                            {row.pct}%
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="h-3 bg-white/[0.04] overflow-hidden">
                      <div
                        className={`h-full ${t.bar} transition-all duration-1000`}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ROI / Revenue Panel */}
          <div className="lg:col-span-5 bg-gradient-to-br from-emerald-500/[0.08] to-[#0a1733] p-8 sm:p-10 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-emerald-500/10 rounded-full blur-3xl" aria-hidden />

            <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-emerald-400 mb-5">
              Operational ROI
            </div>

            <div className="space-y-5">
              <ROIRow label="Lead Investment" value="$3,047.58" tone="rose" strike />
              <ROIRow label="Revenue Generated" value="$4,200.00" tone="emerald" />
              <div className="pt-5 border-t border-white/10">
                <div className="font-sans-body uppercase text-[9px] tracking-[0.28em] text-slate-400 mb-1">
                  Net Return
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl sm:text-6xl text-emerald-400 leading-none">
                    +{roi}%
                  </span>
                  <span className="text-sm text-slate-400">ROI</span>
                </div>
                <p className="mt-3 text-xs text-slate-400 leading-relaxed">
                  Plus brand awareness, pipeline growth, and a nurture list
                  that compounds — none of which the spreadsheet sees.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* OUTCOME BREAKDOWN */}
        <div className="reveal grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-8">
          {OUTCOMES.map((o, i) => {
            const t = TONE[o.tone];
            return (
              <div
                key={o.label}
                data-testid={`outcome-tile-${i}`}
                className="bg-[#0a1733]/80 p-6 lg:p-7"
              >
                <div className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-400 leading-snug mb-4">
                  {o.label}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`font-display text-4xl leading-none ${t.text}`}>
                    {o.value}
                  </span>
                  <span className="text-xs text-slate-500 uppercase tracking-[0.16em]">
                    {o.pct}%
                  </span>
                </div>
                <div className="mt-4 h-1 bg-white/5 overflow-hidden">
                  <div
                    className={`h-full ${t.bar}`}
                    style={{ width: `${Math.min(o.pct * 2, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* NO-SHOW MONSTER — cinematic ad image */}
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch mb-12">
          <div className="md:col-span-7 relative overflow-hidden border border-white/10 min-h-[260px]">
            <img
              src={CAMPAIGN_IMAGES.noShowMonster}
              alt="The hidden no-show predator — solved by reminder automation"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a1733]/40 via-transparent to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
          </div>
          <div className="md:col-span-5 bg-gradient-to-br from-rose-500/[0.06] to-amber-500/[0.04] border border-rose-500/25 p-7 lg:p-9 flex flex-col justify-center">
            <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-rose-300 mb-3">
              The Hidden Predator
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight">
              39 no-shows. <span className="italic text-amber-400">3 in one day.</span>
            </h3>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
              Even with automation working, no-shows still exist — but the
              system <span className="text-white">recovers</span> them.
              Reminder workflows, missed-appointment flows, and re-engagement
              sequences turn every empty slot into a future opportunity.
            </p>
          </div>
        </div>

        {/* EMOTIONAL TAKEAWAY */}
        <div className="reveal max-w-3xl mx-auto p-8 sm:p-10 bg-gradient-to-br from-amber-500/[0.06] to-transparent border-l-2 border-amber-500">
          <div className="font-sans-body uppercase text-[10px] tracking-[0.32em] text-amber-400 mb-3">
            What This Actually Means
          </div>
          <p className="font-display text-2xl sm:text-3xl text-white leading-snug">
            "Optimization isn't a vanity metric. It's the difference between{" "}
            <span className="italic text-amber-400">
              reactive growth and predictable momentum.
            </span>
            "
          </p>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed">
            These systems are designed to create more than appointments —
            they build long-term business infrastructure, visibility,
            nurturing, and scalable growth. You don't have to stay stuck in
            reactive mode.
          </p>
        </div>

        {/* CTA */}
        <div className="reveal mt-12 flex justify-center">
          <a
            href={LINKS.calendar}
            data-testid="proof-cta"
            className="group inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-slate-950 font-semibold px-8 py-4 text-[13px] uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
          >
            I Want Results Like This
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="transition-transform group-hover:translate-x-1">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

function ToplineTile({ label, value, sub, tone }) {
  const t = TONE[tone];
  return (
    <div className="bg-[#0a1733]/80 p-6 sm:p-7 flex flex-col justify-between min-h-[140px]">
      <div className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-400 leading-snug">
        {label}
      </div>
      <div>
        <div className={`font-display text-5xl sm:text-6xl leading-none ${t.text}`}>
          {value}
        </div>
        {sub && (
          <div className="mt-3 text-xs text-slate-400 uppercase tracking-[0.18em]">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function ROIRow({ label, value, tone, strike }) {
  const t = TONE[tone];
  return (
    <div className="flex items-end justify-between gap-4 pb-3 border-b border-white/5 last:border-b-0">
      <span className="font-sans-body uppercase text-[10px] tracking-[0.24em] text-slate-400">
        {label}
      </span>
      <span
        className={`font-display text-3xl ${t.text} ${strike ? "line-through decoration-rose-500/40 decoration-[2px]" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
